import { useMutation, useQuery } from '@tanstack/react-query';

import { clientApi } from '@/config/axios';

export interface SocialProvider {
  client_id: string;
  label: string;
  provider: string;
  redirect_uri: string;
}

interface TokenResponse {
  access_token: string;
  refresh_token: string;
}

/**
 * Which social buttons to render.
 *
 * The API returns only providers it has fully configured, so an absent provider
 * means "not set up here" rather than "temporarily unavailable" — there is
 * nothing to render in a disabled state. `client_id` comes back with it so the
 * browser SDK can initialise without the value being baked into this build.
 */
export function useSocialProviders() {
  return useQuery({
    retry: false,
    // Configuration changes with a deploy, not within a session.
    staleTime: Infinity,
    queryKey: ['social-providers'],
    queryFn: async () => {
      const { data } = await clientApi.get<{ providers: SocialProvider[] }>('/api/v1/auth/social/providers');
      return data.providers;
    },
  });
}

/**
 * Exchange a provider ID token for this app's own tokens.
 *
 * Returns the same shape as password login, so the caller stores tokens through
 * one path regardless of how the person signed in.
 */
export function useSocialSignIn() {
  return useMutation({
    mutationFn: async ({
      idToken,
      provider,
      organizationName,
    }: {
      idToken: string;
      organizationName?: string;
      provider: string;
    }) => {
      const { data } = await clientApi.post<TokenResponse>(`/api/v1/auth/social/${provider}`, {
        id_token: idToken,
        // Only consulted if this sign-in creates an account; ignored otherwise.
        organization_name: organizationName || null,
      });
      return data;
    },
  });
}
